/**
 * Ghost Path (Updated for Scrolling)
 * Flickering uncertain path that diverges from the true regression line
 * Standing on it too long triggers subtle dread
 */

import Phaser from 'phaser';
import { CONSTANTS } from '../config.js';
import { randomRange } from '../systems/Utils.js';

export class GhostPath {
    constructor(scene, regressionLine) {
        this.scene = scene;
        this.regressionLine = regressionLine;

        // Ghost path offset from true path
        this.offset = 0;
        this.targetOffset = 0;
        this.maxOffset = CONSTANTS.GHOST_PATH_OFFSET_MAX;

        // Flicker state
        this.visible = true;
        this.flickerTimer = 0;
        this.flickerRate = 0.1;
        this.flickerInterval = 100;

        // Phase offset for wave motion
        this.phaseOffset = Math.random() * Math.PI * 2;
        this.waveSpeed = 0.002;

        // Graphics
        this.graphics = scene.add.graphics().setDepth(8);

        // Color with transparency for ghostly effect
        this.baseColor = CONSTANTS.COLORS.LINE_GHOST;
        this.currentAlpha = 0.4;

        // Camera tracking
        this.cameraX = 0;
    }

    update(delta, cameraX = 0) {
        this.cameraX = cameraX;

        // Update phase for wave motion
        this.phaseOffset += delta * this.waveSpeed;

        // Update flicker
        this.flickerTimer += delta;
        if (this.flickerTimer >= this.flickerInterval) {
            this.flickerTimer = 0;
            this.flickerInterval = randomRange(50, 150);

            // Random visibility based on flicker rate
            this.visible = Math.random() > this.flickerRate;

            // Occasional alpha variation
            this.currentAlpha = 0.3 + Math.random() * 0.3;
        }

        // Update offset based on regression line variance
        const variance = this.regressionLine.variance;
        this.targetOffset = Math.sin(this.phaseOffset) * variance * this.maxOffset;

        // Smooth offset transition
        this.offset = Phaser.Math.Linear(this.offset, this.targetOffset, 0.05);

        // Render
        this.render();
    }

    render() {
        this.graphics.clear();

        if (!this.visible) return;

        const startX = this.cameraX;
        const endX = this.cameraX + this.scene.scale.width;

        // Ghost line style
        this.graphics.lineStyle(3, this.baseColor, this.currentAlpha);
        this.graphics.beginPath();

        let firstPoint = true;
        for (let x = startX; x <= endX; x += 6) {
            const trueY = this.regressionLine.getYAtX(x, false);

            // Add sinusoidal variation plus offset
            const waveOffset = Math.sin(x * 0.03 + this.phaseOffset) * 15;
            const ghostY = trueY + this.offset + waveOffset;

            if (firstPoint) {
                this.graphics.moveTo(x, ghostY);
                firstPoint = false;
            } else {
                this.graphics.lineTo(x, ghostY);
            }
        }

        this.graphics.strokePath();

        // Add secondary faint line for depth
        this.graphics.lineStyle(6, this.baseColor, this.currentAlpha * 0.3);
        this.graphics.beginPath();

        firstPoint = true;
        for (let x = startX; x <= endX; x += 8) {
            const trueY = this.regressionLine.getYAtX(x, false);
            const waveOffset = Math.sin(x * 0.03 + this.phaseOffset) * 15;
            const ghostY = trueY + this.offset + waveOffset;

            if (firstPoint) {
                this.graphics.moveTo(x, ghostY);
                firstPoint = false;
            } else {
                this.graphics.lineTo(x, ghostY);
            }
        }

        this.graphics.strokePath();
    }

    // Get Y position of ghost path at X
    getYAtX(x) {
        const trueY = this.regressionLine.getYAtX(x, false);
        const waveOffset = Math.sin(x * 0.03 + this.phaseOffset) * 15;
        return trueY + this.offset + waveOffset;
    }

    // Check if player is on ghost path (but NOT on true path)
    isPlayerOnGhostPath(playerX, playerY, tolerance = 30) {
        const ghostY = this.getYAtX(playerX);
        const trueY = this.regressionLine.getYAtX(playerX);

        // Must be close to ghost path
        const nearGhost = Math.abs(playerY - ghostY) < tolerance;

        // Must be far from true path
        const farFromTrue = Math.abs(playerY - trueY) > tolerance * 0.8;

        // Ghost path must be significantly different from true path
        const pathsDifferent = Math.abs(ghostY - trueY) > 25;

        return nearGhost && farFromTrue && pathsDifferent;
    }

    // Get distance from ghost path
    getDistanceFromGhostPath(playerX, playerY) {
        const ghostY = this.getYAtX(playerX);
        return Math.abs(playerY - ghostY);
    }

    // Increase flicker rate (for higher tension)
    setFlickerRate(rate) {
        this.flickerRate = Phaser.Math.Clamp(rate, 0, 0.8);
    }

    destroy() {
        this.graphics.destroy();
    }
}
